import {
  type ApiErrorCode,
  type ApiErrorParams,
  type Capabilities,
  type FolderRecord,
  isApiErrorCode,
  type ModelExtractedFields,
  type PDF_CONTENT_TYPE,
  type PermissionGrantRecord,
  type PermissionLevel,
  type PermissionResourceType,
  type PrincipalType,
  type TeamListing,
  type TeamMemberRecord,
  type TeamRecord,
  type UserListing,
  type VisibleContract,
} from "@contract-manager/shared";
import type { TokenProvider } from "../auth/tokenProvider";

/** Envelope every endpoint answers with (docs/mvp-design.md §5). */
interface ApiEnvelope<T> {
  data: T | null;
  error: string | null;
  code?: unknown;
  params?: unknown;
}

export interface CreateUploadResponse {
  contractId: string;
  uploadUrl: string;
  s3Key: string;
  expiresInSeconds: number;
}

/**
 * Contracts as the API hands them back.
 *
 * `access: "discoverable"` means the caller was told this contract exists so
 * they can ask for it, and the record is redacted — no PDF, no storage
 * location. The screens branch on the tag rather than guessing from a missing
 * field.
 */
export interface ContractPage {
  items: VisibleContract[];
  nextCursor?: string;
}

export interface AbortUploadResponse {
  contractId: string;
}

export interface ContractFileUrl {
  url: string;
  expiresInSeconds: number;
}

/**
 * Folders and teams come back flat; `buildForest` turns either list into the
 * tree the screens draw. The server has no reason to nest them for us — the
 * flat form is what the pickers filter and what the guards run against.
 */
/** Each folder carries what this caller may do to it. */
export type FolderListing = FolderRecord & { access: Capabilities };

export interface FolderList {
  items: FolderListing[];
}

export interface TeamList {
  items: TeamListing[];
}

export interface TeamMemberList {
  items: TeamMemberRecord[];
}

export interface UserList {
  items: UserListing[];
}

/** A team the person is on, carrying the name the screen shows. */
export interface UserTeamMembership {
  teamId: string;
  name: string;
  addedAt: string;
  addedBy: string;
}

export interface UserTeamList {
  items: UserTeamMembership[];
}

export interface PermissionList {
  items: PermissionGrantRecord[];
  /** What the caller themselves may do here. */
  callerAccess: Capabilities;
}

export interface GrantInput {
  principalType: PrincipalType;
  principalId: string;
  level: PermissionLevel;
}

/** Absent and null both mean "at the root". */
export interface NodeInput {
  name?: string;
  parentId?: string | null;
}

/**
 * Failures this client notices itself, as opposed to refusals the API names.
 * They get codes for the same reason: the screens translate a code, never
 * show this file's wording.
 */
export type ClientErrorCode =
  | "REQUEST_FAILED"
  | "RESPONSE_WITHOUT_DATA"
  | "UPLOAD_TRANSFER_FAILED";

export type ErrorCode = ApiErrorCode | ClientErrorCode;

export interface ErrorDetail {
  readonly code: ErrorCode;
  readonly params?: ApiErrorParams;
}

/**
 * Carries the status so a page can tell "signed out" from "server broke", and
 * the code so it can say which in the reader's language. `message` stays the
 * server's own text, for logs and for a code this build does not know yet.
 */
export class ApiError extends Error {
  readonly statusCode: number;
  readonly detail: ErrorDetail | undefined;

  constructor(statusCode: number, message: string, detail?: ErrorDetail) {
    super(message);
    this.name = "ApiError";
    this.statusCode = statusCode;
    this.detail = detail;
  }
}

/** Trust the envelope's code and params only as far as their shape. */
function detailOf(
  envelope: ApiEnvelope<unknown> | null,
): ErrorDetail | undefined {
  if (!isApiErrorCode(envelope?.code)) {
    return undefined;
  }
  const params = envelope?.params;
  if (typeof params !== "object" || params === null) {
    return { code: envelope.code };
  }
  const entries = Object.entries(params).filter(
    (entry): entry is [string, string | number] =>
      typeof entry[1] === "string" || typeof entry[1] === "number",
  );
  return { code: envelope.code, params: Object.fromEntries(entries) };
}

export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly tokens: TokenProvider;
  /** Injected in tests; defaults to the platform fetch. */
  readonly fetchFn?: typeof fetch;
}

const PDF_MIME: typeof PDF_CONTENT_TYPE = "application/pdf";

function pathOf(
  resourceType: PermissionResourceType,
  resourceId: string,
): string {
  const collection = resourceType === "FOLDER" ? "folders" : "contracts";
  return `/${collection}/${encodeURIComponent(resourceId)}`;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly tokens: TokenProvider;
  private readonly fetchFn: typeof fetch;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, "");
    this.tokens = options.tokens;
    this.fetchFn = options.fetchFn ?? globalThis.fetch.bind(globalThis);
  }

  async listContracts(
    params: { limit?: number; cursor?: string } = {},
  ): Promise<ContractPage> {
    const query = new URLSearchParams();
    if (params.limit !== undefined) {
      query.set("limit", `${params.limit}`);
    }
    if (params.cursor !== undefined) {
      query.set("cursor", params.cursor);
    }
    const suffix = query.size > 0 ? `?${query}` : "";
    return this.request<ContractPage>("GET", `/contracts${suffix}`);
  }

  async getContract(contractId: string): Promise<VisibleContract> {
    return this.request<VisibleContract>(
      "GET",
      `/contracts/${encodeURIComponent(contractId)}`,
    );
  }

  async getContractFileUrl(contractId: string): Promise<ContractFileUrl> {
    return this.request<ContractFileUrl>(
      "GET",
      `/contracts/${encodeURIComponent(contractId)}/file`,
    );
  }

  /**
   * Accept what the model read, corrected where it was wrong.
   *
   * Sends every field, not the ones that changed: approving is a statement
   * about all of them (packages/api/src/approveContract.ts). The notice
   * deadline is not among them — the API derives it from what is sent.
   */
  async approveContract(
    contractId: string,
    extracted: ModelExtractedFields,
  ): Promise<VisibleContract> {
    return this.request<VisibleContract>(
      "POST",
      `/contracts/${encodeURIComponent(contractId)}/approval`,
      { extracted },
    );
  }

  /**
   * `folderId` files the contract as it is created, rather than leaving it
   * unfiled and moving it afterwards — a contract that appeared org-wide for a
   * moment on its way into a restricted folder would be a leak, however brief.
   */
  async createUpload(file: {
    name: string;
    size: number;
    folderId?: string | undefined;
  }): Promise<CreateUploadResponse> {
    return this.request<CreateUploadResponse>("POST", "/uploads", {
      originalFileName: file.name,
      contentType: PDF_MIME,
      byteSize: file.size,
      ...(file.folderId === undefined ? {} : { folderId: file.folderId }),
    });
  }

  /**
   * Take back the row `createUpload` wrote when the bytes never arrived.
   *
   * The API creates the record before the browser has sent anything, because
   * the extractor runs off the S3 event and needs somewhere to attach the PDF.
   * Without this call a failed PUT leaves a contract that claims to be
   * uploaded and never extracts.
   */
  async abortUpload(contractId: string): Promise<AbortUploadResponse> {
    return this.request<AbortUploadResponse>(
      "DELETE",
      `/uploads/${encodeURIComponent(contractId)}`,
    );
  }

  /**
   * Send the bytes straight to S3.
   *
   * The Content-Type header is required, not decoration: the API signs the URL
   * with `signableHeaders: ["content-type"]`, so S3 rejects a PUT that omits it
   * or sends a different value.
   */
  async uploadToS3(uploadUrl: string, file: Blob): Promise<void> {
    const response = await this.fetchFn(uploadUrl, {
      method: "PUT",
      headers: { "content-type": PDF_MIME },
      body: file,
    });
    if (!response.ok) {
      throw new ApiError(
        response.status,
        `the upload to S3 failed (${response.status})`,
        {
          code: "UPLOAD_TRANSFER_FAILED",
          params: { status: response.status },
        },
      );
    }
  }

  async listFolders(): Promise<FolderList> {
    return this.request<FolderList>("GET", "/folders");
  }

  async createFolder(input: {
    name: string;
    parentId?: string | null;
  }): Promise<FolderRecord> {
    return this.request<FolderRecord>("POST", "/folders", input);
  }

  async updateFolder(
    folderId: string,
    changes: NodeInput,
  ): Promise<FolderRecord> {
    return this.request<FolderRecord>(
      "PATCH",
      `/folders/${encodeURIComponent(folderId)}`,
      changes,
    );
  }

  async deleteFolder(folderId: string): Promise<{ folderId: string }> {
    return this.request<{ folderId: string }>(
      "DELETE",
      `/folders/${encodeURIComponent(folderId)}`,
    );
  }

  async listFolderContracts(
    folderId: string,
    params: { limit?: number; cursor?: string } = {},
  ): Promise<ContractPage> {
    const query = new URLSearchParams();
    if (params.limit !== undefined) {
      query.set("limit", `${params.limit}`);
    }
    if (params.cursor !== undefined) {
      query.set("cursor", params.cursor);
    }
    const suffix = query.size > 0 ? `?${query}` : "";
    return this.request<ContractPage>(
      "GET",
      `/folders/${encodeURIComponent(folderId)}/contracts${suffix}`,
    );
  }

  /** `null` takes the contract back out of whichever folder it is in. */
  async setContractFolder(
    contractId: string,
    folderId: string | null,
  ): Promise<VisibleContract> {
    return this.request<VisibleContract>(
      "PUT",
      `/contracts/${encodeURIComponent(contractId)}/folder`,
      { folderId },
    );
  }

  async listTeams(): Promise<TeamList> {
    return this.request<TeamList>("GET", "/teams");
  }

  async createTeam(input: {
    name: string;
    parentId?: string | null;
  }): Promise<TeamRecord> {
    return this.request<TeamRecord>("POST", "/teams", input);
  }

  async updateTeam(teamId: string, changes: NodeInput): Promise<TeamRecord> {
    return this.request<TeamRecord>(
      "PATCH",
      `/teams/${encodeURIComponent(teamId)}`,
      changes,
    );
  }

  async deleteTeam(teamId: string): Promise<{ teamId: string }> {
    return this.request<{ teamId: string }>(
      "DELETE",
      `/teams/${encodeURIComponent(teamId)}`,
    );
  }

  async listTeamMembers(teamId: string): Promise<TeamMemberList> {
    return this.request<TeamMemberList>(
      "GET",
      `/teams/${encodeURIComponent(teamId)}/members`,
    );
  }

  async removeTeamMember(
    teamId: string,
    email: string,
  ): Promise<{ teamId: string; email: string }> {
    return this.request<{ teamId: string; email: string }>(
      "DELETE",
      `/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(email)}`,
    );
  }

  /**
   * Record this sign-in and fetch the profile.
   *
   * Also what creates the user row: there is no separate registration step,
   * and calling this is how somebody comes to exist in the directory.
   */
  async me(): Promise<UserListing> {
    return this.request<UserListing>("GET", "/me");
  }

  async listUsers(): Promise<UserList> {
    return this.request<UserList>("GET", "/users");
  }

  async listUserTeams(email: string): Promise<UserTeamList> {
    return this.request<UserTeamList>(
      "GET",
      `/users/${encodeURIComponent(email)}/teams`,
    );
  }

  /**
   * Put someone on a team.
   *
   * Addressed by the person, not the team, and the API refuses an address the
   * directory does not already know — so this is the only way in.
   */
  async joinTeam(email: string, teamId: string): Promise<TeamMemberRecord> {
    return this.request<TeamMemberRecord>(
      "POST",
      `/users/${encodeURIComponent(email)}/teams`,
      { teamId },
    );
  }

  async leaveTeam(
    email: string,
    teamId: string,
  ): Promise<{ email: string; teamId: string }> {
    return this.request<{ email: string; teamId: string }>(
      "DELETE",
      `/users/${encodeURIComponent(email)}/teams/${encodeURIComponent(teamId)}`,
    );
  }

  /**
   * Access grants, addressed by the resource they govern.
   *
   * One trio of calls for both folders and contracts: the API takes the same
   * request either way, and the screens differ only in which noun they show.
   */
  async listPermissions(
    resourceType: PermissionResourceType,
    resourceId: string,
  ): Promise<PermissionList> {
    return this.request<PermissionList>(
      "GET",
      `${pathOf(resourceType, resourceId)}/permissions`,
    );
  }

  /** Granting the same principal again raises or lowers what they hold. */
  async grantPermission(
    resourceType: PermissionResourceType,
    resourceId: string,
    input: GrantInput,
  ): Promise<PermissionGrantRecord> {
    return this.request<PermissionGrantRecord>(
      "PUT",
      `${pathOf(resourceType, resourceId)}/permissions`,
      input,
    );
  }

  async revokePermission(
    resourceType: PermissionResourceType,
    resourceId: string,
    principalType: PrincipalType,
    principalId: string,
  ): Promise<{ resourceId: string; principalId: string }> {
    return this.request<{ resourceId: string; principalId: string }>(
      "DELETE",
      `${pathOf(resourceType, resourceId)}/permissions/${principalType}/${encodeURIComponent(principalId)}`,
    );
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    const accessToken = await this.tokens.getAccessToken();
    const response = await this.fetchFn(`${this.baseUrl}${path}`, {
      method,
      headers: {
        authorization: `Bearer ${accessToken}`,
        ...(body === undefined
          ? {}
          : { "content-type": "application/json; charset=utf-8" }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });

    let envelope: ApiEnvelope<T> | null = null;
    try {
      envelope = (await response.json()) as ApiEnvelope<T>;
    } catch {
      // A 401 from the authorizer is not the API's envelope, so a body that is
      // not JSON is expected rather than exceptional.
      envelope = null;
    }

    if (!response.ok) {
      throw new ApiError(
        response.status,
        envelope?.error ?? `the request failed (${response.status})`,
        detailOf(envelope) ?? {
          code: "REQUEST_FAILED",
          params: { status: response.status },
        },
      );
    }
    if (envelope?.data == null) {
      throw new ApiError(response.status, "the response carried no data", {
        code: "RESPONSE_WITHOUT_DATA",
      });
    }
    return envelope.data;
  }
}
